import { CommunityUserSpaceInteraction } from 'types/user';

import { ApolloCache, useQuery } from '@apollo/client';

import {
  COMMUNITY_USER_SPACE_INTERACTIONS_FRAGMENT,
  CURRENT_USER_QUERY,
} from 'schema/User/queries';

export function useCurrentUser(communityId: string) {
  const { data, loading } = useQuery(CURRENT_USER_QUERY, {
    variables: { communityId },
    fetchPolicy: 'cache-first',
  });

  const interactions: CommunityUserSpaceInteraction[] =
    data?.me?.communityUserSpaceInteractions?.edges?.map(({ node }) => node) || [];

  const getSpaceInteraction = (spaceId: string): CommunityUserSpaceInteraction => {
    return (
      interactions.find((interaction) => interaction.communitySpace.id === spaceId) ?? {
        hasUnreadPosts: false,
        lastReadAt: '',
        communitySpace: { id: spaceId },
      }
    );
  };

  const updateSpaceInteraction = ({
    cache,
    spaceId,
    hasUnreadPosts,
    lastReadAt,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>;
    spaceId: string;
    hasUnreadPosts: boolean;
    lastReadAt: string;
  }) => {
    cache.modify({
      id: cache.identify({ __typename: 'User', id: data?.me?.id }),
      fields: {
        communityUserSpaceInteractions(existingConnection = {}) {
          const existingEdges = existingConnection.edges || [];

          const newNode = {
            __typename: 'CommunityUserSpaceInteraction',
            hasUnreadPosts,
            lastReadAt,
            communitySpace: {
              __typename: 'CommunitySpace',
              id: spaceId,
            },
          };

          const newNodeRef = cache.writeFragment({
            data: newNode,
            fragment: COMMUNITY_USER_SPACE_INTERACTIONS_FRAGMENT,
            fragmentName: 'CommunityUserSpaceInteractionsFields',
          });

          const newEdges = [...(existingEdges || []), { node: newNodeRef }];

          return {
            ...existingConnection,
            edges: newEdges,
          };
        },
      },
    });
  };

  return {
    loading,
    interactions,
    getSpaceInteraction,
    updateSpaceInteraction,
  };
}
